/**
 * Confirm Delete Dialog Component
 *
 * A confirmation dialog that requires the user to type "delete" to confirm.
 *
 * Features:
 * - Requires typing "delete" to enable the button
 * - Loading state support
 * - Destructive variant styling with warning icon
 * - Optional warning message for dependencies
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@loader/ui';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@loader/ui';
import { Input } from '@loader/ui';

interface ConfirmDeleteDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Dialog title (e.g., "Delete Version") */
  title: string;
  /** Optional warning message about dependencies */
  warningMessage?: string;
  /** Whether the operation is in progress */
  isLoading?: boolean;
  /** Callback when user confirms deletion */
  onConfirm: () => void | Promise<void>;
}

/**
 * ConfirmDeleteDialog Component
 *
 * Displays a confirmation dialog that requires the user to type "delete"
 * to confirm. The confirm button is disabled until the text matches.
 *
 * @example
 * ```tsx
 * <ConfirmDeleteDialog
 *   open={deleteDialogOpen}
 *   onOpenChange={setDeleteDialogOpen}
 *   title="Delete Version"
 *   isLoading={isDeleting}
 *   onConfirm={handleConfirmDelete}
 * />
 * ```
 */
export function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title,
  warningMessage,
  isLoading = false,
  onConfirm,
}: ConfirmDeleteDialogProps) {
  const [confirmText, setConfirmText] = useState('');

  // Reset input when dialog closes
  useEffect(() => {
    if (!open) {
      setConfirmText('');
    }
  }, [open]);

  const isConfirmEnabled = !isLoading && confirmText.trim().toLowerCase() === 'delete';

  const handleConfirm = async () => {
    if (!isConfirmEnabled) return;
    await onConfirm();
  };

  const handleCancel = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {warningMessage && (
            <div className="bg-destructive/10 border-destructive/20 rounded-md border p-3">
              <p className="text-destructive text-sm font-medium">
                {warningMessage}
              </p>
            </div>
          )}

          <p className="text-muted-foreground text-sm">
            This action cannot be undone. Type <span className="font-semibold">delete</span> to confirm.
          </p>

          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="delete"
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && isConfirmEnabled) {
                void handleConfirm();
              }
            }}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleConfirm()}
            disabled={!isConfirmEnabled}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
